import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";

const Contact = () => {
  const { language } = useLanguage();

  const content =
    language === "en"
      ? {
          heroTitle: "Contact",
          heroSubtitle:
            "We’re here to answer your questions and help with anything elevator-related.",
          findUs: "Find Us",
          mapTitle: "LIFTCO Location",
          quickHelpTitle: "Need Immediate Help?",
          quickHelpSubtitle: "Call us now for 24/7 service",
          phoneTitle: "Phone",
          emailTitle: "Email",
          addressTitle: "Address",
          hoursTitle: "Working Hours",
          hours1: "24/7 Service",
          hours2: "7 days a week",
        }
      : {
          heroTitle: "Επικοινωνία",
          heroSubtitle:
            "Είμαστε εδώ για να απαντήσουμε στις ερωτήσεις σας και να σας βοηθήσουμε με οποιαδήποτε ανάγκη σχετικά με ανελκυστήρες.",
          findUs: "Βρείτε Μας",
          mapTitle: "LIFTCO Τοποθεσία",
          quickHelpTitle: "Χρειάζεστε Άμεση Βοήθεια;",
          quickHelpSubtitle: "Καλέστε μας τώρα για 24ωρη εξυπηρέτηση",
          phoneTitle: "Τηλέφωνο",
          emailTitle: "Email",
          addressTitle: "Διεύθυνση",
          hoursTitle: "Ώρες Λειτουργίας",
          hours1: "24ωρη Εξυπηρέτηση",
          hours2: "7 ημέρες την εβδομάδα",
        };

  const contactInfo = [
    {
      icon: Phone,
      title: content.phoneTitle,
      details: ["2310 270290", "694 7127721"],
      action: "tel:2310270290",
    },
    {
      icon: Mail,
      title: content.emailTitle,
      details: ["liftco.thessaloniki@gmail.com"],
      action: "mailto:liftco.thessaloniki@gmail.com",
    },
    {
      icon: MapPin,
      title: content.addressTitle,
      details:
        language === "en"
          ? ["Amyndas 18", "Thessaloniki"]
          : ["Αμύντα 18", "Θεσσαλονίκη"],
      action: "https://maps.google.com/?q=Αμύντα+18+Θεσσαλονίκη",
    },
    {
      icon: Clock,
      title: content.hoursTitle,
      details: [content.hours1, content.hours2],
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {content.heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/80 max-w-2xl mx-auto">
              {content.heroSubtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                {info.action ? (
                  <a
                    href={info.action}
                    target={info.action.startsWith("http") ? "_blank" : undefined}
                    rel={info.action.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="block bg-card rounded-xl p-6 shadow-card hover:shadow-lg transition-all duration-300 h-full group"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <info.icon className="h-6 w-6" />
                      </div>
                      <h3 className="font-heading font-semibold text-lg">{info.title}</h3>
                    </div>
                    {info.details.map((detail) => (
                      <p key={detail} className="text-muted-foreground">
                        {detail}
                      </p>
                    ))}
                  </a>
                ) : (
                  <div className="bg-card rounded-xl p-6 shadow-card h-full">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary">
                        <info.icon className="h-6 w-6" />
                      </div>
                      <h3 className="font-heading font-semibold text-lg">{info.title}</h3>
                    </div>
                    {info.details.map((detail) => (
                      <p key={detail} className="text-muted-foreground">
                        {detail}
                      </p>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-heading text-2xl font-bold text-foreground mb-6 text-center">
              {content.findUs}
            </h2>
            <div className="aspect-video min-h-[400px] rounded-xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3027.8!2d22.9487!3d40.6425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14a838f4a3b9c9e7%3A0x1234567890abcdef!2sAmynta%2018%2C%20Thessaloniki!5e0!3m2!1sen!2sgr!4v1703700000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={content.mapTitle}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Call CTA */}
      <section className="py-16 bg-accent">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-accent-foreground mb-2">
                {content.quickHelpTitle}
              </h2>
              <p className="text-accent-foreground/80">{content.quickHelpSubtitle}</p>
            </div>
            <Button
              asChild
              size="lg"
              className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
            >
              <a href="tel:2310270290">
                <Phone className="h-5 w-5" />
                2310 270290
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
